import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSettings } from '../contexts/SettingsContext';
import { useCookieConsent } from '../contexts/CookieConsentContext';

const PIXEL_SCRIPT_ID = 'meta-pixel-loader';

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: any;
  }
}

/**
 * Injeta o Meta Pixel (Facebook/Instagram Ads) e rastreia PageView, só nas páginas públicas e só
 * depois de consentimento de cookies de marketing (LGPD — cookie não essencial exige opt-in).
 * Eventos de conversão (clique de CTA/WhatsApp) são disparados pelas próprias páginas via
 * `trackPixelEvent`, não aqui.
 */
export default function MetaPixel() {
  const location = useLocation();
  const { settings } = useSettings();
  const { consent } = useCookieConsent();
  const analytics = settings?.analytics;
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isFirstTrackedRoute = useRef(true);

  const pixelId = analytics?.metaPixelId?.trim();
  const isActive = !isAdminRoute && !!analytics?.enabled && !!pixelId && consent.marketing;

  useEffect(() => {
    if (!isActive || !pixelId) return;
    if (document.getElementById(PIXEL_SCRIPT_ID)) {
      window.fbq?.('init', pixelId);
      window.fbq?.('track', 'PageView');
      return;
    }

    // Snippet oficial do Meta Pixel (fbevents.js), sem depender de biblioteca externa.
    const fbq: any = function (...args: any[]) {
      (fbq.callMethod ? fbq.callMethod.apply(fbq, args) : fbq.queue.push(args));
    };
    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = window.fbq || fbq;

    const script = document.createElement('script');
    script.id = PIXEL_SCRIPT_ID;
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);

    // Usa a referência local (sempre definida aqui), não window.fbq — TS não estreita o tipo de
    // uma propriedade global opcional só por causa da atribuição acima.
    fbq('init', pixelId);
    fbq('track', 'PageView');
    isFirstTrackedRoute.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, pixelId]);

  useEffect(() => {
    if (!isActive) return;
    if (isFirstTrackedRoute.current) {
      isFirstTrackedRoute.current = false;
      return;
    }
    window.fbq?.('track', 'PageView');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, isActive]);

  return null;
}

/** Dispara um evento de conversão do Pixel (ex.: 'Lead', 'Contact') se o Pixel estiver ativo. */
export function trackPixelEvent(eventName: string, params?: Record<string, unknown>) {
  window.fbq?.('track', eventName, params);
}
